import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { MOTION, motionAnimate } from "./motion";

/**
 * A selection plate that glides between the items of a list (spec 07 › Foco
 * e interacción): the segmented thumb, the sidebar's active plate, the
 * Profile section rail. One absolute node with `ui-indicator` inside the list
 * (the items' offsetParent); the hook writes the active item's place on it as
 * `--ind-pos` / `--ind-size` (the CSS sets `translate` and `width`, `height`
 * on the y axis), at once: the plate is where it goes, at its size, from the
 * first frame. The glide is a FLIP on the compositor: from where it was seen,
 * by `translate` and a `scale` along the axis (origin at its start edge), in
 * 200 ms on --ease-move (120 for a hover plate), so no frame lays it out
 * again and a commit below it (the list a filter changes) never holds it.
 * At rest its pixels are its own (the scale is 1). The items carry
 * `data-indicator-key`.
 *
 * It only glides from one item to another; a new glide starts from where the
 * last one has it on screen. Its first measure, a resize (a ResizeObserver on
 * the list and its items) and anything that moves the items under the same
 * key (a label, a count, the theme) land at once: nothing travels that the
 * person did not move. With reduced motion it jumps (`motionAnimate` moves
 * nothing that does not fade).
 *
 * It never forces a layout its owner's commit did not ask for: a commit reads
 * the items' offsets only to glide (a new key while the plate shows). Its
 * first measure, a reappearance and a new set of items land after the
 * frame's layout (the ResizeObserver's report, before that frame paints),
 * and a commit that changes none of key, items or axis reads nothing (the
 * sidebar under J/K, the Profile rail under «Guardar»): the observer reports
 * what moves them. Without ResizeObserver (jsdom) it measures on each commit.
 */

export type IndicatorAxis = "x" | "y";

export interface IndicatorOptions {
  /** `x` (default): a thumb along a row of segments; `y`: a plate behind a column of items. */
  axis?: IndicatorAxis;
  /**
   * Follow the item under a mouse instead of `activeKey` (desktop rails, P2):
   * it appears on the first item without travelling, moves between items in
   * 120 ms and fades out in 130 when the pointer leaves the list or rests on
   * anything in it that is not an item. A finger never shows it.
   */
  hover?: boolean;
}

export interface Indicator<T extends HTMLElement> {
  /** The plate: `const { ref } = useIndicator(…)`, then `<span ref={ref} className="ui-indicator …" aria-hidden="true" />`. */
  ref: RefObject<T | null>;
}

interface Placed {
  key: string;
  pos: number;
  size: number;
}

interface IndicatorState {
  axis: IndicatorAxis;
  /** The key it follows: `activeKey`, or the item under the mouse. */
  key: string | null;
  /** Where it stands; null until its first measure and while it is hidden. */
  at: Placed | null;
  observer: ResizeObserver | null;
  observed: Element[];
  /** The glide under way, if any. */
  flight: Animation | null;
}

const ITEM = "[data-indicator-key]";

function itemFor(list: HTMLElement, key: string | null): HTMLElement | null {
  if (key == null) return null;
  for (const item of list.querySelectorAll<HTMLElement>(ITEM)) if (item.dataset.indicatorKey === key) return item;
  return null;
}

/** Where the plate is seen now along its axis (mid-glide too), in the list's own offsets. */
function seen(list: HTMLElement, plate: HTMLElement, axis: IndicatorAxis): { pos: number; size: number } {
  const box = plate.getBoundingClientRect();
  const frame = list.getBoundingClientRect();
  return axis === "x"
    ? { pos: box.left - frame.left - list.clientLeft + list.scrollLeft, size: box.width }
    : { pos: box.top - frame.top - list.clientTop + list.scrollTop, size: box.height };
}

/**
 * Puts the plate on its item, or hides it. `change` says a commit or the
 * pointer moved it (a resize never glides).
 */
function sync(list: HTMLElement, plate: HTMLElement, state: IndicatorState, change: boolean): void {
  const item = itemFor(list, state.key);
  if (!item || state.key == null) {
    state.flight?.cancel();
    state.flight = null;
    if (state.at) delete plate.dataset.on;
    state.at = null;
    return;
  }
  // Layout offsets: whole pixels, like the segmented thumb always had, and blind to a press's scale.
  const x = state.axis === "x";
  const pos = x ? item.offsetLeft : item.offsetTop;
  const size = x ? item.offsetWidth : item.offsetHeight;
  const at = state.at;
  if (at && at.key === state.key && at.pos === pos && at.size === size) return;
  const glide = change && at !== null && at.key !== state.key;
  // From where the eye has it: mid-glide, where the last one is.
  const from = !glide ? null : state.flight?.playState === "running" ? seen(list, plate, state.axis) : at;
  state.flight?.cancel();
  state.flight = null;
  plate.style.setProperty("--ind-pos", `${pos}px`);
  plate.style.setProperty("--ind-size", `${size}px`);
  plate.dataset.on = "";
  state.at = { key: state.key, pos, size };
  if (!from || (Math.abs(from.pos - pos) < 0.5 && Math.abs(from.size - size) < 0.5)) return;
  const ratio = from.size / Math.max(size, 1);
  state.flight = motionAnimate(
    plate,
    [
      { translate: x ? `${from.pos}px 0` : `0 ${from.pos}px`, scale: x ? `${ratio} 1` : `1 ${ratio}` },
      { translate: x ? `${pos}px 0` : `0 ${pos}px`, scale: "1 1" },
    ],
    { ms: plate.dataset.hover === undefined ? "base" : "fast", ease: "move" },
  );
}

export function useIndicator<T extends HTMLElement = HTMLSpanElement>(
  listRef: RefObject<HTMLElement | null>,
  activeKey: string | null | undefined,
  { axis = "x", hover = false }: IndicatorOptions = {},
): Indicator<T> {
  const ref = useRef<T | null>(null);
  const state = useRef<IndicatorState>({ axis, key: null, at: null, observer: null, observed: [], flight: null });

  // Every commit: the active key may have changed, or the items under it.
  useLayoutEffect(() => {
    const list = listRef.current;
    const plate = ref.current;
    const s = state.current;
    if (!list || !plate) return;
    const axisMoved = s.axis !== axis;
    if (axisMoved) s.at = null;
    s.axis = axis;
    if (plate.dataset.axis !== axis) plate.dataset.axis = axis;
    if (hover) {
      if (plate.dataset.hover === undefined) plate.dataset.hover = "";
    } else {
      if (plate.dataset.hover !== undefined) delete plate.dataset.hover;
      s.key = activeKey ?? null;
    }
    if (typeof ResizeObserver === "undefined") {
      sync(list, plate, s, true);
      return;
    }
    // A new key while the plate shows glides: measured now, so the first frame has it on its way.
    // Hiding reads nothing either way.
    if (s.key === null || (s.at !== null && s.at.key !== s.key)) sync(list, plate, s, true);
    // The list and its items, watched again only when they changed: a new
    // observer's first report places the plate after layout.
    const items = [list, ...list.querySelectorAll(ITEM)];
    if (!s.observer || items.length !== s.observed.length || items.some((el, i) => el !== s.observed[i])) {
      s.observer?.disconnect();
      s.observer = new ResizeObserver(() => {
        if (listRef.current && ref.current) sync(listRef.current, ref.current, s, false);
      });
      for (const el of items) s.observer.observe(el);
      s.observed = items;
      return;
    }
    // Shown again, or on a new axis: placed after layout (the list reports again), without travelling.
    if (s.key !== null && (s.at === null || axisMoved)) {
      s.observer.unobserve(list);
      s.observer.observe(list);
    }
  });

  useLayoutEffect(() => {
    const s = state.current;
    return () => {
      s.observer?.disconnect();
      s.observer = null;
      s.observed = [];
      s.flight?.cancel();
      s.flight = null;
    };
  }, []);

  // Hover mode: the item under a mouse (a pen or a finger never hover-paints).
  useEffect(() => {
    const list = listRef.current;
    const plate = ref.current;
    if (!hover || !list || !plate) return;
    const s = state.current;
    let fade = 0;
    const follow = (key: string | null) => {
      window.clearTimeout(fade);
      if (key === s.key) return;
      s.key = key;
      sync(list, plate, s, true);
    };
    const over = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || !(event.target instanceof Element)) return;
      const item = event.target.closest<HTMLElement>(ITEM);
      if (item && list.contains(item)) {
        follow(item.dataset.indicatorKey ?? null);
        return;
      }
      // Not an item (a gap, a heading, a divider, the empty end of the nav):
      // it fades out there too, after a beat in which a pointer crossing a gap
      // reaches the neighbour and the plate glides on.
      window.clearTimeout(fade);
      if (s.key !== null) fade = window.setTimeout(() => follow(null), MOTION.ms.instant);
    };
    const leave = (event: PointerEvent) => {
      if (event.pointerType === "mouse") follow(null);
    };
    list.addEventListener("pointerover", over);
    list.addEventListener("pointerleave", leave);
    return () => {
      window.clearTimeout(fade);
      list.removeEventListener("pointerover", over);
      list.removeEventListener("pointerleave", leave);
    };
  }, [hover, listRef]);

  return { ref };
}
